import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { apiService } from '@/services/api';

/**
 * Custom hook for managing cars and car types
 *
 * UPDATED: Now uses apiService with request cancellation
 */
export const useCars = (API) => {
  const [cars, setCars] = useState([]);
  const [carTypes, setCarTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Fetch cars
   * Now includes request cancellation
   */
  const fetchCars = useCallback(async () => {
    const controller = new AbortController();

    setLoading(true);
    setError(null);

    try {
      const response = await apiService.get('/admin/cars', {
        signal: controller.signal
      });
      setCars(response.data);
    } catch (err) {
      if (err.name === 'CanceledError' || err.code === 'ERR_CANCELED') {
        return;
      }

      setError(err);
      toast.error('Failed to fetch cars');
    } finally {
      setLoading(false);
    }

    return () => controller.abort();
  }, []);

  /**
   * Fetch car types
   * Now includes request cancellation
   */
  const fetchCarTypes = useCallback(async () => {
    const controller = new AbortController();

    setLoading(true);

    try {
      const response = await apiService.get('/admin/car-types', {
        signal: controller.signal
      });
      setCarTypes(response.data);
    } catch (err) {
      if (err.name === 'CanceledError' || err.code === 'ERR_CANCELED') {
        return;
      }

      toast.error('Failed to fetch car types');
    } finally {
      setLoading(false);
    }

    return () => controller.abort();
  }, []);

  /**
   * Create car
   */
  const createCar = useCallback(async (carData) => {
    try {
      const amenitiesArray = carData.amenities
        ? carData.amenities.split(',').map(a => a.trim()).filter(a => a)
        : [];

      await apiService.post('/admin/cars', {
        ...carData,
        base_daily_rate: parseFloat(carData.base_daily_rate),
        amenities: amenitiesArray
      });

      toast.success('Car added successfully');
      await fetchCars();
      return { success: true };
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add car');
      return { success: false, error: err };
    }
  }, [fetchCars]);

  /**
   * Update car
   */
  const updateCar = useCallback(async (carId, carData) => {
    try {
      const amenitiesArray = carData.amenities
        ? carData.amenities.split(',').map(a => a.trim()).filter(a => a)
        : undefined;

      await apiService.put(`/admin/cars/${carId}`, {
        ...carData,
        base_daily_rate: carData.base_daily_rate ? parseFloat(carData.base_daily_rate) : undefined,
        amenities: amenitiesArray
      });

      toast.success('Car updated successfully');
      await fetchCars();
      return { success: true };
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update car');
      return { success: false, error: err };
    }
  }, [fetchCars]);

  /**
   * Delete car
   */
  const deleteCar = useCallback(async (carId) => {
    try {
      await apiService.delete(`/admin/cars/${carId}`);
      toast.success('Car deleted successfully');
      await fetchCars();
      return { success: true };
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete car');
      return { success: false, error: err };
    }
  }, [fetchCars]);

  /**
   * Toggle car status
   */
  const toggleCarStatus = useCallback(async (carId) => {
    try {
      await apiService.patch(`/admin/cars/${carId}/status`);
      toast.success('Car status updated successfully');
      await fetchCars();
      return { success: true };
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update car status');
      return { success: false, error: err };
    }
  }, [fetchCars]);

  /**
   * Create car type
   */
  const createCarType = useCallback(async (carTypeData) => {
    try {
      await apiService.post('/admin/car-types', {
        ...carTypeData,
        base_daily_rate: parseFloat(carTypeData.base_daily_rate)
      });

      toast.success('Car type created successfully');
      await fetchCarTypes();
      await fetchCars(); // Refresh cars in case any were affected
      return { success: true };
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create car type');
      return { success: false, error: err };
    }
  }, [fetchCarTypes, fetchCars]);

  /**
   * Update car type
   */
  const updateCarType = useCallback(async (carTypeId, carTypeData) => {
    try {
      await apiService.put(`/admin/car-types/${carTypeId}`, {
        ...carTypeData,
        base_daily_rate: parseFloat(carTypeData.base_daily_rate)
      });

      toast.success('Car type updated successfully');
      await fetchCarTypes();
      return { success: true };
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update car type');
      return { success: false, error: err };
    }
  }, [fetchCarTypes]);

  /**
   * Delete car type
   */
  const deleteCarType = useCallback(async (carTypeId) => {
    try {
      await apiService.delete(`/admin/car-types/${carTypeId}`);
      toast.success('Car type deleted successfully');
      await fetchCarTypes();
      await fetchCars();
      return { success: true };
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete car type');
      return { success: false, error: err };
    }
  }, [fetchCarTypes, fetchCars]);

  /**
   * Get car status (check if currently rented)
   */
  const getCarStatus = useCallback((carId, bookings) => {
    const now = new Date();
    const rentedBooking = bookings.find(booking =>
      booking.car_id === carId &&
      booking.payment_status === 'paid' &&
      new Date(booking.car_start_date) <= now &&
      new Date(booking.car_end_date) >= now
    );
    return rentedBooking ? 'rented' : 'available';
  }, []);

  /**
   * Get car type name
   */
  const getCarTypeName = useCallback((carTypeId) => {
    const carType = carTypes.find(ct => ct.id === carTypeId);
    return carType ? carType.name : 'Unknown';
  }, [carTypes]);

  /**
   * Filter cars by type
   */
  const filterCarsByType = useCallback((carTypeId) => {
    if (carTypeId === 'all') return cars;
    return cars.filter(car => car.car_type_id === carTypeId);
  }, [cars]);

  /**
   * Initial fetch with cleanup
   */
  useEffect(() => {
    const cleanupCars = fetchCars();
    const cleanupCarTypes = fetchCarTypes();

    return () => {
      if (cleanupCars && typeof cleanupCars === 'function') {
        cleanupCars();
      }
      if (cleanupCarTypes && typeof cleanupCarTypes === 'function') {
        cleanupCarTypes();
      }
    };
  }, [fetchCars, fetchCarTypes]);

  return {
    // Data
    cars,
    carTypes,
    loading,
    error,

    // Cars operations
    fetchCars,
    createCar,
    updateCar,
    deleteCar,
    toggleCarStatus,

    // Car types operations
    fetchCarTypes,
    createCarType,
    updateCarType,
    deleteCarType,

    // Utilities
    getCarStatus,
    getCarTypeName,
    filterCarsByType,
  };
};
